import { useState, useEffect, useCallback } from 'react';
import { useAuthenticatedApi } from './useAuthenticatedApi';

interface Deployment {
  id: string;
  workspace_id: string;
  git_commit_hash: string;
  status: 'NOT_STARTED' | 'RUNNING' | 'COMPLETE' | 'FAILED';
  created_at: string;
  updated_at: string;
}

interface DeploymentStep {
  id: string;
  deployment_id: string;
  infrastructure_id: string;
  infrastructure_element_name: string;
  status: 'NOT_STARTED' | 'RUNNING' | 'COMPLETE' | 'FAILED';
  gitlab_pipeline_id: number | null;
  gitlab_pipeline_status: string | null;
  gitlab_pipeline_url: string | null;
  created_at: string;
  updated_at: string;
}

interface CreateDeploymentResponse {
  deployment: Deployment;
  deployment_steps: DeploymentStep[];
  task_id: string;
}

interface UseDeploymentsReturn {
  deployments: Deployment[];
  deploymentSteps: Record<string, DeploymentStep[]>;
  loading: boolean;
  error: string | null;
  creatingDeployment: boolean;
  fetchDeployments: () => Promise<void>;
  createDeployment: (gitCommitHash: string) => Promise<CreateDeploymentResponse>;
  fetchDeploymentSteps: (deploymentId: string) => Promise<DeploymentStep[]>;
}

export const useDeployments = (workspaceId: string | undefined): UseDeploymentsReturn => {
  const [deployments, setDeployments] = useState<Deployment[]>([]);
  const [deploymentSteps, setDeploymentSteps] = useState<Record<string, DeploymentStep[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creatingDeployment, setCreatingDeployment] = useState(false);
  const { get, post } = useAuthenticatedApi();

  const fetchDeployments = useCallback(async () => {
    if (!workspaceId) return;

    try {
      setLoading(true);
      setError(null);

      const response = await get(`/workspaces/${workspaceId}/deployments`);

      // Update deployments and automatically refresh steps for deployments
      // whose status has changed
      setDeployments(prevDeployments => {
        // Check which deployments have changed status
        const changedDeployments = response.filter((newDeployment: Deployment) => {
          const prevDeployment = prevDeployments.find(d => d.id === newDeployment.id);
          return prevDeployment && prevDeployment.status !== newDeployment.status;
        });

        // For deployments that have changed status and have steps loaded, 
        // refresh their steps asynchronously
        changedDeployments.forEach((deployment: Deployment) => {
          if (deploymentSteps[deployment.id]) {
            get(`/workspaces/${workspaceId}/deployments/${deployment.id}/steps`)
              .then((steps: DeploymentStep[]) => {
                setDeploymentSteps(prev => ({ ...prev, [deployment.id]: steps }));
              })
              .catch(err => {
                console.error(`Error refreshing steps for deployment ${deployment.id}:`, err);
              });
          }
        });

        return response;
      });

      return response;
    } catch (err) {
      console.error('Error fetching deployments:', err);
      setError('Failed to load deployments');
    } finally {
      setLoading(false);
    }
  }, [workspaceId, get, deploymentSteps]);

  const createDeployment = useCallback(async (gitCommitHash: string): Promise<CreateDeploymentResponse> => {
    if (!workspaceId) {
      throw new Error('Workspace ID is required');
    }

    setCreatingDeployment(true);
    try {
      const response = await post(`/workspaces/${workspaceId}/deployments`, {
        git_commit_hash: gitCommitHash.trim()
      });

      // Refresh deployments list
      await fetchDeployments();

      return response;
    } catch (err: any) {
      console.error('Error creating deployment:', err);
      throw new Error(err.response?.data?.detail || 'Failed to create deployment');
    } finally {
      setCreatingDeployment(false);
    }
  }, [workspaceId, post, fetchDeployments]);

  const fetchDeploymentSteps = useCallback(async (deploymentId: string): Promise<DeploymentStep[]> => {
    if (!workspaceId) {
      throw new Error('Workspace ID is required');
    }

    // Return cached steps if already loaded
    if (deploymentSteps[deploymentId]) {
      return deploymentSteps[deploymentId];
    }

    try {
      const steps = await get(`/workspaces/${workspaceId}/deployments/${deploymentId}/steps`);
      setDeploymentSteps(prev => ({ ...prev, [deploymentId]: steps }));
      return steps;
    } catch (err) {
      console.error('Error fetching deployment steps:', err);
      throw new Error('Failed to load deployment steps');
    }
  }, [workspaceId, get, deploymentSteps]);

  useEffect(() => {
    fetchDeployments();
  }, [fetchDeployments]);

  return {
    deployments,
    deploymentSteps,
    loading,
    error,
    creatingDeployment,
    fetchDeployments,
    createDeployment,
    fetchDeploymentSteps
  };
}; 